import { apiClient, ApiResponse } from '@/lib/api-client';
import { API_CONFIG } from '@/config/api';
import { Category } from '@/types';

/**
 * Categories API response data structure
 */
interface CategoriesData {
  categories: Category[];
  total_categories: number;
}

/**
 * Service for handling category-related API calls
 */
export class CategoryService {
  /**
   * Fetch all categories from the API
   * @returns Promise with categories data
   */
  static async getCategories(): Promise<CategoriesData> {
    const response = await apiClient.get<ApiResponse<CategoriesData>>(
      API_CONFIG.ENDPOINTS.CATEGORIES
    );

    if (!response.status) {
      throw new Error(response.message || 'Failed to fetch categories');
    }

    return response.data;
  }

  /**
   * Fetch a single category by ID
   * @param id - Category ID
   * @returns Promise with category data
   */
  static async getCategoryById(id: number): Promise<Category> {
    const response = await apiClient.get<ApiResponse<{ category: Category }>>(
      `${API_CONFIG.ENDPOINTS.CATEGORIES}/${id}`
    );

    if (!response.status) {
      throw new Error(response.message || 'Failed to fetch category');
    }

    return response.data.category;
  }

  /**
   * Fetch a category by slug
   * @param slug - Category slug
   * @returns Promise with category data
   */
  static async getCategoryBySlug(slug: string): Promise<Category> {
    const response = await apiClient.get<ApiResponse<{ category: Category }>>(
      `${API_CONFIG.ENDPOINTS.CATEGORIES}/${slug}`
    );

    if (!response.status) {
      throw new Error(response.message || 'Failed to fetch category');
    }

    return response.data.category;
  }
}

// Export default instance for convenience
export const categoryService = CategoryService;
